import { useState, useEffect } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { useNavigate, useLocation } from 'react-router-dom'
import { AlertTriangle, X, Check } from 'lucide-react'
import { useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'
import SearchableChecklist from '../../components/SearchableChecklist'
import { MEDICATION_ALLERGIES, FOOD_ALLERGIES, CONTACT_ALLERGIES } from '../../data/medicalLists'
import { resolveProfileReturn } from '../../utils/profileReturnTo'

function YesNoPills({ value, onYes, onNo, t }) {
  return (
    <div className="flex gap-3 mb-4">
      <button onClick={onYes}
        className={`flex-1 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all ${value === true ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-400 border-[1.5px] border-gray-200'}`}>
        {t('common.yes')}
      </button>
      <button onClick={onNo}
        className={`flex-1 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all ${value === false ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-400 border-[1.5px] border-gray-200'}`}>
        {t('common.no')}
      </button>
    </div>
  )
}

export default function ProfileAllergies() {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, updateProfile, syncSection } = useProfileStore()
  const { t } = useI18n()
  
  const [hasMedAllergies, setHasMedAllergies] = useState(null)
  const [medAllergies, setMedAllergies] = useState([])
  const [expandedMed, setExpandedMed] = useState(null)
  const [hasFoodAllergies, setHasFoodAllergies] = useState(null)
  const [foodAllergies, setFoodAllergies] = useState([])
  const [expandedFood, setExpandedFood] = useState(null)
  const [hasContactAllergies, setHasContactAllergies] = useState(null)
  const [contactAllergies, setContactAllergies] = useState([])
  const [otherContactAllergy, setOtherContactAllergy] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [modalType, setModalType] = useState('medication')
  const [newAllergy, setNewAllergy] = useState({ name: '', severity: 'Mild', reaction: '' })

  useEffect(() => {
    if (profile?.allergies) {
      setHasMedAllergies(profile.allergies.hasMedAllergies ?? null)
      const savedMedList = profile.allergies.medList || []
      setMedAllergies(savedMedList.map(item => typeof item === 'string' ? { id: `custom-${item}`, name: item, severity: 'Unknown', custom: true } : item))
      setHasFoodAllergies(profile.allergies.hasFoodAllergies ?? null)
      const savedFoodList = profile.allergies.foodList || []
      setFoodAllergies(savedFoodList.map(item => typeof item === 'string' ? { id: `custom-${item}`, name: item, severity: 'Unknown', custom: true } : item))
      setHasContactAllergies(profile.allergies.hasContactAllergies ?? null)
      setContactAllergies(profile.allergies.contactList || [])
      setOtherContactAllergy(profile.allergies.otherContactAllergy || '')
    }
  }, [profile])

  const handleSave = async () => {
    const allergyData = {
      hasMedAllergies, medList: medAllergies,
      hasFoodAllergies, foodList: foodAllergies,
      hasContactAllergies, contactList: contactAllergies,
      otherContactAllergy: contactAllergies.includes('other') ? otherContactAllergy : '',
      noKnownAllergies: hasMedAllergies === false && hasFoodAllergies === false && hasContactAllergies === false,
      list: [...medAllergies, ...foodAllergies],
      completed: true,
    }
    const result = await syncSection('allergies', allergyData)
    if (!result.success) updateProfile({ allergies: allergyData })
    // ★ P7 P2 (TEST 2.09) — back to the reopen draft, not always the Profile tab.
    const { path, state } = resolveProfileReturn(location.state)
    navigate(path, { state })
  }

  // Toggle handlers
  const toggleMedAllergy = (item) => {
    const existing = medAllergies.find(a => a.id === item.id)
    if (existing) { setMedAllergies(medAllergies.filter(a => a.id !== item.id)); setExpandedMed(null) }
    else { setMedAllergies([...medAllergies, { ...item, severity: 'Mild', reaction: '' }]); setExpandedMed(item.id); setHasMedAllergies(true) }
  }
  const toggleFoodAllergy = (item) => {
    const existing = foodAllergies.find(a => a.id === item.id)
    if (existing) { setFoodAllergies(foodAllergies.filter(a => a.id !== item.id)); setExpandedFood(null) }
    else { setFoodAllergies([...foodAllergies, { ...item, severity: 'Mild', reaction: '' }]); setExpandedFood(item.id); setHasFoodAllergies(true) }
  }
  const toggleContactAllergy = (item) => {
    const id = item.id
    if (contactAllergies.includes(id)) setContactAllergies(contactAllergies.filter(x => x !== id))
    else setContactAllergies([...contactAllergies, id])
  }

  const openAddModal = (type) => { setModalType(type); setNewAllergy({ name: '', severity: 'Mild', reaction: '' }); setShowAddModal(true) }
  const handleAddCustomAllergy = () => {
    if (newAllergy.name.trim()) {
      const custom = { id: `custom-${Date.now()}`, name: newAllergy.name.trim(), severity: newAllergy.severity, reaction: newAllergy.reaction, custom: true }
      if (modalType === 'medication') { setMedAllergies([...medAllergies, custom]); setHasMedAllergies(true) }
      else { setFoodAllergies([...foodAllergies, custom]); setHasFoodAllergies(true) }
      setNewAllergy({ name: '', severity: 'Mild', reaction: '' }); setShowAddModal(false)
    }
  }

  const canSave = hasMedAllergies !== null && hasFoodAllergies !== null && hasContactAllergies !== null
  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] bg-white transition-colors"

  // Expanded content for allergy items — severity + reaction
  const renderAllergyExpanded = (item, setter, list) => (
    <div className="space-y-2 mt-2">
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('profileAllergies.severity')}</label>
        <select value={item.severity || 'Mild'} onChange={(e) => setter(list.map(a => a.id === item.id ? { ...a, severity: e.target.value } : a))}
          className={inputCls} style={{ color: '#15354F' }}>
          <option value="Mild">{t('profileAllergies.mild')}</option><option value="Moderate">{t('profileAllergies.moderate')}</option><option value="Severe">{t('profileAllergies.severe')}</option>
        </select>
      </div>
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">{t('profileAllergies.reactionOptional')}</label>
        <input type="text" value={item.reaction || ''} onChange={(e) => setter(list.map(a => a.id === item.id ? { ...a, reaction: e.target.value } : a))}
          placeholder={t('profileAllergies.reactionPlaceholderFull')}
          className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: '#15354F' }} />
      </div>
    </div>
  )

  return (
    <>
      <ProfileSubpageLayout sectionId="allergies" icon={AlertTriangle} onSave={handleSave} saving={false} canSave={canSave}>
        {/* MEDICATION ALLERGIES */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileAllergies.medicationAllergies')}</p>

        <YesNoPills value={hasMedAllergies} onYes={() => setHasMedAllergies(true)} onNo={() => { setHasMedAllergies(false); setMedAllergies([]) }} t={t} />

        {hasMedAllergies && (
          <div className="mb-5">
            <SearchableChecklist
              items={MEDICATION_ALLERGIES}
              selectedItems={medAllergies}
              onToggle={toggleMedAllergy}
              onRemoveCustom={(id) => setMedAllergies(medAllergies.filter(a => a.id !== id))}
              onAddCustom={() => openAddModal('medication')}
              addLabel="Add other medication"
              placeholder={t('profileAllergies.searchMedications')}
              selectedColor="amber"
              renderExpanded={(item) => renderAllergyExpanded(item, setMedAllergies, medAllergies)}
              expandedId={expandedMed}
              onExpand={setExpandedMed}
            />
          </div>
        )}

        <div className="border-t border-gray-100 my-5" />

        {/* FOOD ALLERGIES */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileAllergies.foodAllergies')}</p>

        <YesNoPills value={hasFoodAllergies} onYes={() => setHasFoodAllergies(true)} onNo={() => { setHasFoodAllergies(false); setFoodAllergies([]) }} t={t} />

        {hasFoodAllergies && (
          <div className="mb-5">
            <SearchableChecklist
              items={FOOD_ALLERGIES}
              selectedItems={foodAllergies}
              onToggle={toggleFoodAllergy}
              onRemoveCustom={(id) => setFoodAllergies(foodAllergies.filter(a => a.id !== id))}
              onAddCustom={() => openAddModal('food')}
              addLabel="Add other food"
              placeholder={t('profileAllergies.searchFoods')}
              selectedColor="amber"
              renderExpanded={(item) => renderAllergyExpanded(item, setFoodAllergies, foodAllergies)}
              expandedId={expandedFood}
              onExpand={setExpandedFood}
            />
          </div>
        )}

        <div className="border-t border-gray-100 my-5" />

        {/* CONTACT ALLERGIES */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileAllergies.contactAllergies')}</p>

        <YesNoPills value={hasContactAllergies} onYes={() => setHasContactAllergies(true)} onNo={() => { setHasContactAllergies(false); setContactAllergies([]) }} t={t} />

        {hasContactAllergies && (
          <div className="mb-5">
            <div className="space-y-1.5">
              {CONTACT_ALLERGIES.map((option) => (
                <button key={option.id} onClick={() => toggleContactAllergy(option)}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[14px] border-[1.5px] transition-colors ${contactAllergies.includes(option.id) ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
                  <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center ${contactAllergies.includes(option.id) ? 'border-[#46A8F9] bg-[#46A8F9]' : 'border-gray-300'}`}>
                    {contactAllergies.includes(option.id) && <Check size={12} className="text-white" />}
                  </div>
                  <span className={`text-[13px] ${contactAllergies.includes(option.id) ? 'font-semibold' : 'font-medium text-gray-600'}`} style={contactAllergies.includes(option.id) ? { color: '#15354F' } : {}}>
                    {option.name}
                  </span>
                </button>
              ))}
            </div>
            {contactAllergies.includes('other') && (
              <div className="mt-3">
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileAllergies.pleaseSpecify')}</label>
                <input type="text" value={otherContactAllergy} onChange={(e) => setOtherContactAllergy(e.target.value)}
                  placeholder={t('profileAllergies.contactPlaceholder')}
                  className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: '#15354F' }} />
              </div>
            )}
          </div>
        )}
      </ProfileSubpageLayout>

      {/* Add Custom Allergy Modal */}
      {showAddModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{t('common.add', 'Add')} {modalType === 'medication' ? 'Medication' : 'Food'} Allergy</h3>
              <button onClick={() => setShowAddModal(false)}><X className="text-gray-400" size={22} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{modalType === 'medication' ? 'Medication Name' : 'Food Name'}</label>
                <input type="text" value={newAllergy.name} onChange={(e) => setNewAllergy({...newAllergy, name: e.target.value})}
                  placeholder={modalType === 'medication' ? 'e.g., Metformin' : 'e.g., Avocado'}
                  className={inputCls} style={{ color: '#15354F' }} autoFocus />
              </div>
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileAllergies.severity')}</label>
                <select value={newAllergy.severity} onChange={(e) => setNewAllergy({...newAllergy, severity: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
                  <option value="Mild">{t('profileAllergies.mild')}</option><option value="Moderate">{t('profileAllergies.moderate')}</option><option value="Severe">{t('profileAllergies.severe')}</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileAllergies.reactionOptional')}</label>
                <input type="text" value={newAllergy.reaction} onChange={(e) => setNewAllergy({...newAllergy, reaction: e.target.value})}
                  placeholder={t('profileAllergies.reactionPlaceholder')} className={inputCls} style={{ color: '#15354F' }} />
              </div>
              <button onClick={handleAddCustomAllergy} disabled={!newAllergy.name.trim()}
                className={`w-full py-3 rounded-2xl font-bold text-[14px] ${newAllergy.name.trim() ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                style={newAllergy.name.trim() ? { background: '#15354F' } : {}}>{t('profileAllergies.add')}</button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}
    </>
  )
}
